import { useQuery } from '@tanstack/react-query';
import {
  TrendingUp,
  TrendingDown,
  Activity,
  Target,
  Sigma,
  GitFork,
} from 'lucide-react';
import { api } from '@/lib/api';
import { fmtMoney, fmtPercent, fmtSignedPercent } from '@/lib/format';
import { cn } from '@/lib/cn';

export function PerformanceMetrics() {
  const q = useQuery({ queryKey: ['performance'], queryFn: api.performance, refetchInterval: 60_000 });
  const d = q.data;

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4 space-y-3">
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-medium text-zinc-200">Performance</h3>
        <span className="text-[11px] text-zinc-400">Time-weighted vs money-weighted vs simple</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <Metric
          icon={TrendingUp}
          label="TWRR"
          value={d ? fmtSignedPercent(d.returns.twrr * 100) : '—'}
          hint="annualised, neutralises cashflow timing"
          tone={(d?.returns.twrr ?? 0) >= 0 ? 'pos' : 'neg'}
        />
        <Metric
          icon={Target}
          label="MWRR (IRR)"
          value={d ? fmtSignedPercent(d.returns.mwrr * 100) : '—'}
          hint="annualised, your skill incl. timing"
          tone={(d?.returns.mwrr ?? 0) >= 0 ? 'pos' : 'neg'}
        />
        <Metric
          icon={Activity}
          label="Simple"
          value={d ? fmtSignedPercent(d.returns.simple * 100) : '—'}
          hint={d ? `True gain ${fmtMoney(d.returns.trueGain)}` : ''}
          tone={(d?.returns.simple ?? 0) >= 0 ? 'pos' : 'neg'}
        />
        <Metric
          icon={Sigma}
          label="Volatility"
          value={d ? fmtPercent(d.risk.volatility) : '—'}
          hint="annualised σ"
        />
        <Metric
          icon={GitFork}
          label="Sharpe / Sortino"
          value={d ? `${d.risk.sharpe.toFixed(2)} / ${d.risk.sortino.toFixed(2)}` : '—'}
          hint="rf=0"
        />
        <Metric
          icon={TrendingDown}
          label="Max DD · β"
          value={d ? `${fmtPercent(d.risk.maxDrawdown)} · ${d.risk.beta.toFixed(2)}` : '—'}
          hint="vs FTSE-100 synth"
          tone={d && d.risk.maxDrawdown > 15 ? 'neg' : undefined}
        />
      </div>

      <div className="text-[10px] text-zinc-400 border-t border-zinc-900 pt-2">
        TWRR shows your *trading skill* return (cashflow-neutral). MWRR (IRR)
        shows the realised return of your money — depends when you added it.
        Simple is end-vs-start adjusted for net deposits.
      </div>
    </div>
  );
}

function Metric({
  icon: Icon,
  label,
  value,
  hint,
  tone,
}: {
  icon: typeof TrendingUp;
  label: string;
  value: string;
  hint?: string;
  tone?: 'pos' | 'neg';
}) {
  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-900/30 px-3 py-2">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-zinc-400">
        <Icon className="size-3" />
        {label}
      </div>
      <div
        className={cn(
          'text-base num font-medium mt-0.5',
          tone === 'pos' ? 'text-emerald-400' : tone === 'neg' ? 'text-rose-400' : 'text-zinc-100',
        )}
      >
        {value}
      </div>
      {hint && <div className="text-[10px] text-zinc-400 mt-0.5">{hint}</div>}
    </div>
  );
}
